import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export function countActiveFilters<F extends Record<string, any>>(filters: F): number {
    return Object.values(filters).filter((v) =>
        Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== ''
    ).length;
}

export interface ManagementTableState<F> {
    tab: string;
    search: string;
    filters: F;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    page: number;
    pageSize: number;
}

export interface UseManagementTableStateOptions<F extends Record<string, any>> {
    emptyFilters: F;
    defaultTab?: string;
    defaultPageSize?: number;
    defaultSortBy?: string;
    defaultSortDir?: 'asc' | 'desc';
    searchDebounceMs?: number;
    /**
     * Called exactly once per settled state change (after search debounce
     * settles, and immediately for tab/filter/sort/page changes), with an
     * AbortSignal that fires if a newer state change supersedes this one
     * before the request finishes — so a slow response can never overwrite
     * a fresher one.
     */
    onStateChange: (state: ManagementTableState<F>, signal: AbortSignal) => void;
}

export interface UseManagementTableStateResult<F extends Record<string, any>> {
    /** The settled state actually driving the fetch (search is debounced). */
    state: ManagementTableState<F>;
    /** Raw, un-debounced search box value — bind this to the input, not state.search. */
    searchInput: string;
    setSearchInput: (value: string) => void;
    /**
     * The filter drawer's in-progress edits. Deliberately separate from
     * `state.filters`: editing the drawer must not refetch the list, only
     * applyDraft() commits.
     */
    draftFilters: F;
    /** Replaces the whole draft (the drawer's per-field edits). */
    setDraftFilters: (filters: F) => void;
    /** Discards draft edits back to what's applied — call when opening the drawer. */
    syncDraft: () => void;
    /** Commits the draft to the applied filters and refetches from page 1. */
    applyDraft: () => void;
    setTab: (tab: string) => void;
    /** Replaces the applied filter set directly (e.g. removing a single chip). */
    setFilters: (filters: F) => void;
    /** "Clear All" — drops every advanced filter, applied and draft alike. */
    clearFilters: () => void;
    /** Clears search + every advanced filter (the filtered-empty-state action). */
    clearSearchAndFilters: () => void;
    toggleSort: (key: string) => void;
    setPage: (page: number) => void;
    setPageSize: (size: number) => void;
    activeFilterCount: number;
    hasActiveSearchOrFilters: boolean;
}

/**
 * Single source of truth for a management-list page's tab/search/advanced
 * filters/sort/pagination — owns the fetch trigger so every settled change
 * fires exactly one request and cancels whatever request was still in flight.
 * Search is debounced before it becomes part of the settled state;
 * tab/sort/filter/page changes are not.
 *
 * State is deliberately component-local and never mirrored into the browser
 * URL: filter selections must not appear in the address bar, and no table
 * interaction may touch history or trigger a navigation/remount. The query
 * string exists only on the outbound API request (see lib/buildListParams).
 */
export function useManagementTableState<F extends Record<string, any>>(
    options: UseManagementTableStateOptions<F>
): UseManagementTableStateResult<F> {
    const {
        emptyFilters,
        defaultTab = 'all',
        defaultPageSize = 10,
        defaultSortBy,
        defaultSortDir,
        searchDebounceMs = 350,
        onStateChange,
    } = options;

    // `emptyFilters` is a module-level constant at every call site, but pin it
    // to a ref so clearFilters() can never close over a stale/recreated object.
    const emptyFiltersRef = useRef(emptyFilters);
    emptyFiltersRef.current = emptyFilters;

    const [state, setState] = useState<ManagementTableState<F>>(() => ({
        tab: defaultTab,
        search: '',
        filters: emptyFilters,
        sortBy: defaultSortBy,
        sortDir: defaultSortDir,
        page: 1,
        pageSize: defaultPageSize,
    }));

    const [searchInput, setSearchInput] = useState('');
    const [draftFilters, setDraftFilters] = useState<F>(emptyFilters);

    // Latest-value mirrors for the stable callbacks below.
    const stateRef = useRef(state);
    stateRef.current = state;
    const draftRef = useRef(draftFilters);
    draftRef.current = draftFilters;

    // Debounce: only the search box waits. Tab/filter/sort/page changes call
    // setState directly and are never delayed.
    useEffect(() => {
        const timer = setTimeout(() => {
            setState((prev) => (prev.search === searchInput ? prev : { ...prev, search: searchInput, page: 1 }));
        }, searchDebounceMs);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    // The one and only effect that reacts to a settled state change: fires the
    // fetch, aborting the previous in-flight request if state changes again
    // before it resolves. Note `draftFilters` is intentionally NOT a dependency
    // — drawer edits must not refetch.
    const onStateChangeRef = useRef(onStateChange);
    onStateChangeRef.current = onStateChange;
    useEffect(() => {
        const controller = new AbortController();
        onStateChangeRef.current(state, controller.signal);
        return () => controller.abort();
    }, [state]);

    const setTab = useCallback((tab: string) => {
        setState((prev) => (prev.tab === tab ? prev : { ...prev, tab, page: 1 }));
    }, []);

    const setFilters = useCallback((filters: F) => {
        setDraftFilters(filters);
        setState((prev) => ({ ...prev, filters, page: 1 }));
    }, []);

    // Read through refs rather than closing over the values, so both callbacks
    // stay referentially stable AND the state updaters stay pure (React may
    // invoke an updater more than once — it must never have side effects).
    const syncDraft = useCallback(() => {
        setDraftFilters(stateRef.current.filters);
    }, []);

    const applyDraft = useCallback(() => {
        const draft = draftRef.current;
        setState((prev) => ({ ...prev, filters: draft, page: 1 }));
    }, []);

    const clearFilters = useCallback(() => {
        setDraftFilters(emptyFiltersRef.current);
        setState((prev) => ({ ...prev, filters: emptyFiltersRef.current, page: 1 }));
    }, []);

    const clearSearchAndFilters = useCallback(() => {
        setSearchInput('');
        setDraftFilters(emptyFiltersRef.current);
        setState((prev) => ({ ...prev, search: '', filters: emptyFiltersRef.current, page: 1 }));
    }, []);

    const toggleSort = useCallback((key: string) => {
        setState((prev) => ({
            ...prev,
            sortBy: key,
            sortDir: prev.sortBy === key && prev.sortDir === 'asc' ? 'desc' : 'asc',
            page: 1,
        }));
    }, []);

    const setPage = useCallback((page: number) => {
        setState((prev) => (prev.page === page ? prev : { ...prev, page }));
    }, []);

    const setPageSize = useCallback((size: number) => {
        setState((prev) => (prev.pageSize === size ? prev : { ...prev, pageSize: size, page: 1 }));
    }, []);

    const activeFilterCount = useMemo(() => countActiveFilters(state.filters), [state.filters]);
    const hasActiveSearchOrFilters = !!state.search || activeFilterCount > 0;

    return {
        state,
        searchInput,
        setSearchInput,
        draftFilters,
        setDraftFilters,
        syncDraft,
        applyDraft,
        setTab,
        setFilters,
        clearFilters,
        clearSearchAndFilters,
        toggleSort,
        setPage,
        setPageSize,
        activeFilterCount,
        hasActiveSearchOrFilters,
    };
}
